// Instanced grass around the camera, swaying in the evening breeze. Each instance is a small tuft
// of thin, curved blades. Tufts wrap around a square patch that follows the camera; each keeps a
// fixed world spot while it is inside the patch, so nothing swims. Lanes get short, mown grass
// that is thinner on the worn running strip; the apron and meadow get longer blades. Blade count
// follows the quality tier (none on Low).
import {
  BufferAttribute, BufferGeometry, Color, DoubleSide, InstancedBufferAttribute,
  InstancedBufferGeometry, Mesh, MeshStandardMaterial, type Camera,
} from 'three';
import { LANE_WIDTH, TRACK_LENGTH } from '../../data/track';
import { GATES, HOARDING, STAND, TRACK_HALF_WIDTH, rng } from './layout';
import { GLSL_FLOOD_REACH, SEASON_UNIFORMS } from './seasonUniforms';
import { GLSL_NOISE, GLSL_TURF } from './util';

/** Blades in one instanced tuft. */
export const BLADES_PER_TUFT = 3;

/**
 * One tuft: BLADES_PER_TUFT blades, each 1 unit tall with its origin at the root. A blade keeps
 * most of its width up to half height and then narrows to the tip (3 triangles). `aTuft` holds
 * each blade's turn around the tuft, height scale and root offset in metres.
 */
function tuftGeometry(): BufferGeometry {
  const r = rng(7);
  const pos: number[] = [], tuft: number[] = [], idx: number[] = [];
  for (let b = 0; b < BLADES_PER_TUFT; b++) {
    const turn = (b / BLADES_PER_TUFT) * Math.PI * 2 + r() * 1.2;
    const scale = 0.75 + r() * 0.45;
    const ox = (r() - 0.5) * 0.03, oz = (r() - 0.5) * 0.03;
    const v = pos.length / 3;
    pos.push(-0.5, 0, 0, 0.5, 0, 0, -0.36, 0.5, 0, 0.36, 0.5, 0, 0, 1, 0);
    for (let i = 0; i < 5; i++) tuft.push(turn, scale, ox, oz);
    idx.push(v, v + 1, v + 2, v + 1, v + 3, v + 2, v + 2, v + 3, v + 4);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('normal', new BufferAttribute(new Float32Array(pos.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute('aTuft', new BufferAttribute(new Float32Array(tuft), 4));
  g.setIndex(idx);
  return g;
}

export class Grass {
  readonly mesh: Mesh<InstancedBufferGeometry, MeshStandardMaterial>;
  private uniforms = {
    uTime: { value: 0 },
    uCenter: { value: [0, 0] as [number, number] },
    uPatch: { value: 40 },
  };

  /** `blades` is the total blade count; it is drawn as tufts of BLADES_PER_TUFT. */
  constructor(blades: number) {
    const base = tuftGeometry();
    const geo = new InstancedBufferGeometry();
    geo.index = base.index;
    for (const name of ['position', 'normal', 'aTuft']) geo.setAttribute(name, base.getAttribute(name));
    const m = new MeshStandardMaterial({ side: DoubleSide, roughness: 0.8, color: new Color(1, 1, 1) });
    m.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.uniforms, { uSnow: SEASON_UNIFORMS.uSnow, uDryGrass: SEASON_UNIFORMS.uDryGrass, uFlood: SEASON_UNIFORMS.uFlood });
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
attribute vec4 aBlade; // tuft: patch-local x, z in 0..1, random, random
attribute vec4 aTuft;  // blade in the tuft: turn, height scale, root offset x, z
uniform float uTime;
uniform vec2 uCenter;
uniform float uPatch;
varying vec3 vGrassColor;
varying float vGrassH;
${GLSL_NOISE}
${GLSL_TURF}
${GLSL_FLOOD_REACH}
const float HALF_W = ${TRACK_HALF_WIDTH.toFixed(3)};
const float LANE_W = ${LANE_WIDTH.toFixed(3)};
const float TRACK_LEN = ${TRACK_LENGTH.toFixed(1)};
const float HOARD_Z = ${HOARDING.z.toFixed(3)};
const float STAND_Z = ${(-STAND.frontZ).toFixed(3)};
const float GATE_D = ${GATES.depth.toFixed(3)};
`)
        .replace('#include <beginnormal_vertex>', `
vec2 local = aBlade.xy * uPatch;
vec2 wp = local + uPatch * floor((uCenter - local) / uPatch + 0.5);
float d = length(wp - uCenter) / (uPatch * 0.5);
float fade = 1.0 - smoothstep(0.55, 1.0, d);
float az = abs(wp.y);
// Each blade gets its own random number from the tuft's and its own attributes.
float br = fract(sin(dot(aBlade.zw * 13.7 + aTuft.xy, vec2(12.9898, 78.233))) * 43758.5453);
// Height: mown lanes, worn centre strips, longer apron and meadow.
float hgt;
float wide = 1.0;
if (az < HALF_W) {
  float inLane = abs(fract((wp.y + HALF_W) / LANE_W) - 0.5) * LANE_W; // distance from lane centre
  float worn = 1.0 - smoothstep(0.15, 0.55, inLane + (envNoise(wp * 0.7) - 0.5) * 0.4);
  hgt = mix(0.07, 0.11, aBlade.z) * (1.0 - worn * 0.6);
  hgt *= step(0.07, LANE_W * 0.5 - inLane); // nothing growing through the edging boards
  hgt *= step(0.6, abs(wp.x - TRACK_LEN));   // keep the finish stripe clean
  hgt *= step(0.3, abs(wp.x - 0.12)); // keep the start line clean
  if (wp.x < 0.0 && wp.x > -GATE_D) hgt = 0.0;
} else {
  float meadow = smoothstep(HALF_W, HALF_W + 3.0, az);
  hgt = mix(0.1, 0.24, meadow) * (0.7 + aBlade.z * 0.6) * (0.75 + envNoise(wp * 0.15) * 0.5);
  wide = mix(1.0, 1.35, meadow);
  if (abs(az - HOARD_Z) < 0.25) hgt = 0.0;
  if (wp.y < -STAND_Z + 0.3) hgt = 0.0; // under the grandstand
}
// Winter: snow buries the meadow; on the cleared lanes the grass stays a little shorter.
hgt *= 1.0 - uSnow * (az < HALF_W ? 0.35 : 0.9);
hgt *= fade * aTuft.y;
vec2 root = wp + aTuft.zw;
vGrassH = position.y;
float ang = aBlade.w * 6.2831 + aTuft.x;
vec3 bladeDir = vec3(cos(ang), 0.0, sin(ang));
vec3 facing = vec3(-sin(ang), 0.0, cos(ang));
// Wind: a slow travelling gust plus per-blade flutter.
float gust = envNoise(wp * 0.08 + vec2(uTime * 0.35, uTime * 0.1));
float y2 = position.y * position.y;
float bend = (gust * 0.55 + sin(uTime * 2.3 + br * 9.0 + wp.x * 0.4) * 0.12) * y2;
// Nearly upward normals so blades light like the turf they grow from. The sun is low, so any
// real sideways tilt would make blades facing away from it turn black. Both faces use the same
// normal (see the fragment shader) for the same reason.
vec3 objectNormal = normalize(facing * 0.08 + vec3(0.0, 1.0, 0.0));
// Thin blades, wider far away so they stay a few pixels wide instead of shimmering.
float camD = distance(vec3(root.x, 0.0, root.y), cameraPosition);
float wdt = (0.008 + br * 0.005) * wide * clamp(0.5 + camD * 0.18, 1.0, 3.5);
// Blades splay out of the tuft and curve over.
float lean = (0.18 + br * 0.3) * y2 * hgt;
vec3 bp = vec3(root.x, 0.0, root.y) + bladeDir * position.x * wdt + vec3(0.0, position.y * hgt, 0.0) + facing * lean;
bp.x += bend * hgt * 0.9;
bp.z += bend * hgt * 0.35;
bp.y -= (bend * bend * 0.3 + lean * 0.5) * hgt;
// Same colour field as the turf underneath, so blades and ground read as one lawn,
// with a few yellower and bluer blades mixed in.
vec3 base = turfColour(envFbm(wp * 0.22 + 3.1), envFbm(wp * 0.035));
if (az < HALF_W) base *= mix(0.88, 1.1, step(0.5, fract(wp.x / 12.0)));
base *= mix(vec3(0.9, 1.0, 1.08), vec3(1.2, 1.08, 0.65), smoothstep(0.55, 1.0, br));
vGrassColor = base * (0.88 + aBlade.z * 0.24);
vGrassColor = mix(vGrassColor, vec3(0.62, 0.66, 0.7), uSnow * (az < HALF_W ? 0.12 : 0.35)); // frost
// At night the meadow beyond the floodlights falls dark with the ground it grows from (lanes.ts).
vGrassColor *= floodReach(vec3(root.x, 0.0, root.y));
#ifdef USE_TANGENT
vec3 objectTangent = vec3(tangent.xyz);
#endif
`)
        .replace('#include <begin_vertex>', 'vec3 transformed = bp;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vGrassColor;\nvarying float vGrassH;')
        .replace('#include <color_fragment>', `#include <color_fragment>
// Darker at the root, where the lawn shades itself, and lighter, warmer tips.
diffuseColor.rgb = vGrassColor * mix(0.65, 1.25, vGrassH) + vec3(0.05, 0.05, 0.0) * vGrassH * vGrassH;`)
        // Double-sided materials flip the normal on back faces, which points it into the ground
        // for these mostly-upward normals. Keep the same normal on both faces.
        .replace('#include <normal_fragment_begin>', `#include <normal_fragment_begin>
normal = normalize(vNormal);
nonPerturbedNormal = normal;`);
    };
    m.customProgramCacheKey = () => 'grass-tufts';
    this.mesh = new Mesh(geo, m);
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = true;
    this.mesh.name = 'grass-blades';
    this.setCount(blades);
  }

  setCount(blades: number): void {
    const geo = this.mesh.geometry;
    const tufts = Math.ceil(blades / BLADES_PER_TUFT);
    this.mesh.visible = tufts > 0;
    if (tufts <= 0) { geo.instanceCount = 0; return; }
    // Bigger patch for more blades, keeping density in a sensible range.
    this.uniforms.uPatch.value = blades >= 300_000 ? 40 : 28;
    const r = rng(99);
    const a = new Float32Array(tufts * 4);
    for (let i = 0; i < tufts * 4; i++) a[i] = r();
    geo.setAttribute('aBlade', new InstancedBufferAttribute(a, 4));
    geo.instanceCount = tufts;
  }

  /** Centres the patch a little ahead of the camera, where it is looking. */
  update(timeS: number, camera: Camera): void {
    this.uniforms.uTime.value = timeS;
    const e = camera.matrixWorld.elements;
    const fx = -e[8]!, fz = -e[10]!;
    const len = Math.hypot(fx, fz) || 1;
    const ahead = this.uniforms.uPatch.value * 0.32;
    this.uniforms.uCenter.value = [camera.position.x + (fx / len) * ahead, camera.position.z + (fz / len) * ahead];
  }
}
